import { parseAmount } from "./convertNumbers";

const STABLE_SYMBOLS = new Set(["USDT", "USDC"]);

export const STABLE_DUST_MIN = 0.001;

const FIAT_CURRENCIES = new Set([
  "ZAR",
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "AUD",
  "CAD",
  "CHF",
  "CNY",
]);

export function isFiatCurrency(asset) {
  if (!asset) return false;
  return FIAT_CURRENCIES.has(asset.toUpperCase());
}

export function determineSellAssets(transaction) {
  const asset = transaction?.asset?.toUpperCase();
  const currency = transaction?.currency?.toUpperCase();

  if (asset && currency) {
    const assetIsFiat = isFiatCurrency(asset);
    const currencyIsFiat = isFiatCurrency(currency);

    if (assetIsFiat && !currencyIsFiat) {
      return { outgoingAsset: currency, incomingAsset: asset };
    }
    if (!assetIsFiat && currencyIsFiat) {
      return { outgoingAsset: asset, incomingAsset: currency };
    }
    if (!assetIsFiat && !currencyIsFiat) {
      return { outgoingAsset: asset, incomingAsset: currency || "ZAR" };
    }
    return {
      outgoingAsset: asset || currency,
      incomingAsset: currency || asset || "ZAR",
    };
  }

  return {
    outgoingAsset: asset || currency || "",
    incomingAsset: currency || asset || "ZAR",
  };
}

function resolveUserTradeLegAssets(tx, subtype) {
  let incoming = tx.transactionRef?.incomingAsset?.toUpperCase() || "";
  let outgoing = tx.transactionRef?.outgoingAsset?.toUpperCase() || "";

  if (!incoming || !outgoing) {
    if (subtype === "buy") {
      incoming =
        incoming || tx.asset?.toUpperCase() || tx.currency?.toUpperCase() || "";
      outgoing =
        outgoing || tx.currency?.toUpperCase() || tx.asset?.toUpperCase() || "";
    } else if (subtype === "sell") {
      const sellAssets = determineSellAssets(tx);
      incoming = incoming || sellAssets.incomingAsset;
      outgoing = outgoing || sellAssets.outgoingAsset;
    }
  }

  return { incoming, outgoing };
}

function userTradeBuyStableLegValue(tx) {
  const r = tx.transactionRef;
  const cryptoAmount = parseAmount(
    r?.incomingAmount?.$numberDecimal ||
      r?.incomingAmount ||
      r?.total?.$numberDecimal ||
      r?.totalAmount?.$numberDecimal
  );
  const price = parseAmount(r?.price?.$numberDecimal || r?.price || 0);
  if (price > 0 && cryptoAmount > 0) {
    return cryptoAmount * price;
  }
  return parseAmount(
    r?.outgoingAmount?.$numberDecimal ||
      r?.outgoingAmount ||
      r?.total?.$numberDecimal ||
      r?.totalAmount?.$numberDecimal ||
      r?.amount?.$numberDecimal ||
      r?.amount ||
      0
  );
}

function userTradeSellStableIncomingValue(tx) {
  const r = tx.transactionRef;
  return parseAmount(
    r?.incomingAmount?.$numberDecimal ||
      r?.incomingAmount ||
      r?.total?.$numberDecimal ||
      r?.totalAmount?.$numberDecimal ||
      r?.amount?.$numberDecimal ||
      0
  );
}

function nonTradeStableAmount(tx) {
  return parseAmount(
    tx.transactionRef?.amount?.$numberDecimal ||
      tx.transactionRef?.amount ||
      tx.transactionRef?.totalAmount?.$numberDecimal ||
      tx.transactionRef?.total?.$numberDecimal ||
      tx.amount ||
      0
  );
}

export function isStablecoinDustTx(tx) {
  if (tx.transactionType === "UserTrade") {
    const subtype = tx.transactionSubtype?.toLowerCase();
    const { incoming, outgoing } = resolveUserTradeLegAssets(tx, subtype);

    if (subtype === "buy" && STABLE_SYMBOLS.has(outgoing)) {
      return userTradeBuyStableLegValue(tx) < STABLE_DUST_MIN;
    }
    if (subtype === "sell" && STABLE_SYMBOLS.has(incoming)) {
      return userTradeSellStableIncomingValue(tx) < STABLE_DUST_MIN;
    }
    return false;
  }

  const asset = (tx.asset || tx.cryptoAsset || tx.currency || "").toUpperCase();
  if (!STABLE_SYMBOLS.has(asset)) {
    return false;
  }

  return nonTradeStableAmount(tx) < STABLE_DUST_MIN;
}

export function withoutStablecoinDust(transactions) {
  const list = transactions || [];
  return list.filter((tx) => !isStablecoinDustTx(tx));
}
